import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const MapWrapper = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const MapTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  color: #2c3e50;
  font-size: 1.8rem;
`;

const MapDescription = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: #7f8c8d;
  font-size: 1.1rem;
`;

// Custom marker icon
const customIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyQzIgMTcuNTIgNi40OCAyMiAxMiAyMkMxNy41MiAyMiAyMiAxNy41MiAyMiAxMkMyMiA2LjQ4IDE3LjUyIDIgMTIgMloiIGZpbGw9IiNkNGFmMzciLz4KPHBhdGggZD0iTTEyIDZDNi40OCA2IDIgMTAuNDggMiAxNkMyIDIxLjUyIDYuNDggMjYgMTIgMjZDMjEuNTIgMjYgMjYgMjEuNTIgMjYgMTZDMjYgMTAuNDggMjEuNTIgNiAxMiA2WiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDEwQzE0LjIwOTEgMTAgMTYgMTEuNzkwOSAxNiAxNEMxNiAxNi4yMDkxIDE0LjIwOTEgMTggMTIgMThDOS43OTA5IDE4IDggMTYuMjA5MSA4IDE0QzggMTEuNzkwOSA5Ljc5MDkgMTAgMTIgMTBaIiBmaWxsPSIjZDRhZjM3Ii8+Cjwvc3ZnPgo=',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

interface Restaurant {
  id: number;
  name: string;
  address: string;
  phone: string;
  coordinates: [number, number];
  openingHours: string;
  hasTakeaway: boolean;
  hasReservation: boolean;
}

const restaurants: Restaurant[] = [
  {
    id: 1,
    name: "Poule & Poulette Brussel Centrum",
    address: "Grote Markt 15, 1000 Brussel",
    phone: "+32 2 123 45 67",
    coordinates: [50.8503, 4.3517],
    openingHours: "11:00 - 22:00",
    hasTakeaway: true,
    hasReservation: true,
  },
  {
    id: 2,
    name: "Poule & Poulette Antwerpen",
    address: "Meir 45, 2000 Antwerpen",
    phone: "+32 3 123 45 67",
    coordinates: [51.2194, 4.4025],
    openingHours: "11:00 - 22:00",
    hasTakeaway: true,
    hasReservation: true,
  },
  {
    id: 3,
    name: "Poule & Poulette Gent",
    address: "Korenmarkt 12, 9000 Gent",
    phone: "+32 9 123 45 67",
    coordinates: [51.0543, 3.7174],
    openingHours: "11:00 - 22:00",
    hasTakeaway: true,
    hasReservation: true,
  },
  {
    id: 4,
    name: "Poule & Poulette Leuven",
    address: "Oude Markt 8, 3000 Leuven",
    phone: "+32 16 123 45 67",
    coordinates: [50.8798, 4.7005],
    openingHours: "11:00 - 22:00",
    hasTakeaway: true,
    hasReservation: true,
  },
  {
    id: 5,
    name: "Poule & Poulette Brugge",
    address: "Markt 20, 8000 Brugge",
    phone: "+32 50 123 45 67",
    coordinates: [51.2093, 3.2247],
    openingHours: "11:00 - 22:00",
    hasTakeaway: true,
    hasReservation: true,
  },
];

const InteractiveMap: React.FC = () => {
  const navigate = useNavigate();

  const handleReservation = (restaurantId: number) => {
    navigate(`/reserveren?restaurant=${restaurantId}`);
  };

  const handleTakeaway = (restaurantId: number) => {
    navigate(`/takeaway?restaurant=${restaurantId}`);
  };

  return (
    <div>
      <MapTitle>Vind een Poule & Poulette bij jou in de buurt</MapTitle>
      <MapDescription>
        Klik op een marker om meer informatie te zien en direct te reserveren of takeaway te bestellen
      </MapDescription>
      
      <MapWrapper>
        <MapContainer
          center={[50.8503, 4.3517]}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {restaurants.map((restaurant) => (
            <Marker
              key={restaurant.id}
              position={restaurant.coordinates}
              icon={customIcon}
            >
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <h3 style={{ color: '#d4af37', margin: '0 0 8px 0' }}>
                    {restaurant.name}
                  </h3>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    📍 {restaurant.address}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    📞 {restaurant.phone}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    🕒 {restaurant.openingHours}
                  </p>
                  
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexDirection: 'column' }}>
                    {restaurant.hasReservation && (
                      <button
                        onClick={() => handleReservation(restaurant.id)}
                        style={{
                          background: '#d4af37',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        🍽️ Reserveren
                      </button>
                    )}
                    
                    {restaurant.hasTakeaway && (
                      <button
                        onClick={() => handleTakeaway(restaurant.id)}
                        style={{
                          background: '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        🥡 Takeaway
                      </button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </MapWrapper>
    </div>
  );
};

export default InteractiveMap;
